// Audio notification utility for transcription events

// Create audio context for generating notification sounds
const createNotificationSound = (frequency, duration, type = 'sine') => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
    oscillator.type = type;
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration);
    
    return audioContext;
  } catch (error) {
    console.warn('Audio notification not supported:', error);
    return null;
  }
};

// Success notification - pleasant ascending chime
export const playSuccessNotification = () => {
  try {
    // Play a pleasant success chime (C-E-G chord progression)
    setTimeout(() => createNotificationSound(523.25, 0.2), 0);   // C5
    setTimeout(() => createNotificationSound(659.25, 0.2), 100); // E5
    setTimeout(() => createNotificationSound(783.99, 0.3), 200); // G5
  } catch (error) {
    console.warn('Could not play success notification:', error);
  }
};

// Error notification - attention-getting but not harsh
export const playErrorNotification = () => {
  try {
    // Play a gentle error sound (descending tone)
    setTimeout(() => createNotificationSound(400, 0.15, 'triangle'), 0);
    setTimeout(() => createNotificationSound(300, 0.15, 'triangle'), 150);
    setTimeout(() => createNotificationSound(250, 0.2, 'triangle'), 300);
  } catch (error) {
    console.warn('Could not play error notification:', error);
  }
};

// Browser notification (if permission granted)
export const showBrowserNotification = async (title, message, type = 'info') => {
  if ('Notification' in window && Notification.permission === 'granted') {
    const icon = type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️';
    const options = {
      body: message,
      icon: '/favicon.ico',
      tag: 'transcription-notification'
    };

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.showNotification(`${icon} ${title}`, options);
      } else {
        new Notification(`${icon} ${title}`, options);
      }
    } catch (error) {
      console.error('Error showing notification:', error);
      // Fallback for when showNotification fails for other reasons.
      try {
        new Notification(`${icon} ${title}`, options);
      } catch (fallbackError) {
        console.error('Error showing fallback notification:', fallbackError);
      }
    }
  }
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if ('Notification' in window && Notification.permission === 'default') {
    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.warn('Could not request notification permission:', error);
      return false;
    }
  }
  return Notification.permission === 'granted';
};

// Combined notification function
export const notifyTranscriptionComplete = async (success = true, message = '') => {
  try {
    if (success) {
      playSuccessNotification();
      await showBrowserNotification('Transcription Complete', message || 'Your audio has been successfully transcribed!', 'success');
    } else {
      playErrorNotification();
      await showBrowserNotification('Transcription Failed', message || 'There was an error transcribing your audio.', 'error');
    }
  } catch (error) {
    console.error("Failed to show notification:", error);
  }
};
